/**
 * <s-tilt-tile>
 * Pointer-driven 3D tilt with a magnifier zoom (collection tiles, product gallery).
 * data-max-tilt="0" turns it into a pure magnifier.
 * Writes --tilt-x / --tilt-y (deg) and --pointer-x / --pointer-y (%) on itself
 * while a fine pointer hovers; the owning block's CSS turns those into the
 * card tilt, the zoom origin, and the light sheen. Reduced motion gets the static tile.
 * Touch devices: tiles with data-swipe-turn flip between front and back view
 * (data-active) when the shopper swipes sideways across them, since there is no
 * hover. A plain tap still opens the product; the click right after a swipe is swallowed.
 */
const SWIPE_MIN_PX = 28;

class STiltTile extends HTMLElement {
  #query = null;
  #frame = 0;
  #maxTilt = 8;
  #ac = null;
  #onMove = (event) => this.#queue(event);
  #onLeave = () => this.#reset();

  connectedCallback() {
    const touch = window.matchMedia('(hover: none)');
    if (this.dataset.swipeTurn !== undefined && touch.matches) this.#listenSwipe();

    this.#query = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
    if (!this.#query.matches) return;
    this.#maxTilt = this.dataset.maxTilt === undefined ? 8 : Number(this.dataset.maxTilt) || 0;
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerleave', this.#onLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerleave', this.#onLeave);
    cancelAnimationFrame(this.#frame);
    this.#ac?.abort();
  }

  #listenSwipe() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let swiped = false;

    this.addEventListener('touchstart', (event) => {
      const touchPoint = event.touches[0];
      startX = touchPoint.clientX;
      startY = touchPoint.clientY;
      tracking = event.touches.length === 1;
      swiped = false;
    }, { passive: true, signal });

    this.addEventListener('touchmove', (event) => {
      if (!tracking) return;
      const touchPoint = event.touches[0];
      const dx = touchPoint.clientX - startX;
      const dy = touchPoint.clientY - startY;
      // Vertical movement means the shopper is scrolling the page: stop tracking this gesture.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
        tracking = false;
        return;
      }
      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      tracking = false;
      swiped = true;
      this.toggleAttribute('data-active');
    }, { passive: true, signal });

    // A swipe ends with a synthetic click on the card link; cancel it so the swipe doesn't navigate.
    this.addEventListener('click', (event) => {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });
  }

  #queue(event) {
    const { clientX, clientY } = event;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update(clientX, clientY));
  }

  #update(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1);
    this.style.setProperty('--tilt-x', `${((0.5 - y) * this.#maxTilt * 2).toFixed(2)}deg`);
    this.style.setProperty('--tilt-y', `${((x - 0.5) * this.#maxTilt * 2).toFixed(2)}deg`);
    this.style.setProperty('--pointer-x', `${(x * 100).toFixed(1)}%`);
    this.style.setProperty('--pointer-y', `${(y * 100).toFixed(1)}%`);
    this.toggleAttribute('data-active', true);
  }

  #reset() {
    cancelAnimationFrame(this.#frame);
    this.removeAttribute('data-active');
    this.style.setProperty('--tilt-x', '0deg');
    this.style.setProperty('--tilt-y', '0deg');
  }
}

if (!customElements.get('s-tilt-tile')) customElements.define('s-tilt-tile', STiltTile);
